import Link from 'next/link'

import type { VehicleSummary } from '@/types'

import {
    ArrowUpRight,
    Check,
    Leaf,
    Luggage,
    Star,
    Users,
    Zap
} from 'lucide-react'
import { buttonClass } from '@/lib/ui'
import { formatPrice, pluralize } from '@/lib/format'
import { getPowertrainKind, getVehicleName } from '@/lib/vehicle-helpers'
import VehicleImage from '@/components/vehicles/vehicle-image'

interface VehicleCardProps {
    vehicle: VehicleSummary
    onReserve?: (vehicle: VehicleSummary) => void
    preload?: boolean
    headingLevel?: 'h2' | 'h3'
}

const AMENITIES_ON_CARD = 3

export default function VehicleCard({ vehicle, onReserve, preload = false, headingLevel = 'h3' }: VehicleCardProps) {
    const Heading = headingLevel
    const powertrain = getPowertrainKind(vehicle.motorizacion)
    const href = `/vehiculos/${vehicle.slug}`
    const reserveClass = buttonClass('primary', 'sm', 'h-10 px-5')
    const amenities = vehicle.equipamiento.slice(0, AMENITIES_ON_CARD)

    const reserveLabel = (
        <>
            Reservar
            <span className='sr-only'>
                {' '}
                <span data-mdlfy-cms={`vehiculos:${vehicle.id}:marca`}>{vehicle.marca}</span>
                {' '}
                <span data-mdlfy-cms={`vehiculos:${vehicle.id}:modelo`}>{vehicle.modelo}</span>
            </span>
        </>
    )

    return (
        <article className='group flex h-full flex-col overflow-hidden rounded-2xl border border-border/80 bg-card shadow-[0_1px_2px_rgb(0_0_0/0.04)] transition-all duration-500 hover:-translate-y-1 hover:border-gold/30 hover:shadow-[0_24px_50px_-28px_rgb(40_30_10/0.45)] motion-reduce:transition-none motion-reduce:hover:translate-y-0'>
            <div className='relative aspect-[16/10] overflow-hidden bg-muted'>
                <Link href={href} tabIndex={-1} aria-hidden='true' className='absolute inset-0 block'>
                    <VehicleImage
                        src={vehicle.imagen}
                        alt={vehicle.imagen_alt || getVehicleName(vehicle)}
                        categoria={vehicle.categoria}
                        sizes='(min-width: 1280px) 400px, (min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw'
                        preload={preload}
                        className='transition-transform duration-700 ease-out group-hover:scale-[1.04] motion-reduce:transition-none motion-reduce:group-hover:scale-100'
                    />
                </Link>

                <div className='pointer-events-none absolute inset-x-0 top-0 flex flex-wrap items-start gap-2 p-4'>
                    <span className='pointer-events-auto rounded-full bg-card/90 px-3 py-1 text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-foreground shadow-sm backdrop-blur'>
                        <span data-mdlfy-cms={`vehiculos:${vehicle.id}:categoria`}>{vehicle.categoria}</span>
                    </span>

                    {vehicle.destacado && (
                        <span className='inline-flex items-center gap-1 rounded-full bg-foreground/90 px-3 py-1 text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-champagne shadow-sm'>
                            <Star className='size-3 fill-current' aria-hidden='true' />
                            Destacado
                        </span>
                    )}
                </div>

                {powertrain && vehicle.motorizacion && (
                    <span className='absolute bottom-4 left-4 inline-flex items-center gap-1.5 rounded-full bg-foreground/80 px-3 py-1 text-xs font-medium text-background backdrop-blur'>
                        {powertrain === 'electric'
                            ? <Zap className='size-3.5 text-champagne' aria-hidden='true' />
                            : <Leaf className='size-3.5 text-champagne' aria-hidden='true' />}
                        <span data-mdlfy-cms={`vehiculos:${vehicle.id}:motorizacion`}>{vehicle.motorizacion}</span>
                    </span>
                )}
            </div>

            <div className='flex flex-1 flex-col p-6'>
                <Heading className='leading-tight'>
                    <Link href={href} className='group/title block rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4 focus-visible:ring-offset-card'>
                        <span className='block text-[0.7rem] font-semibold uppercase tracking-[0.22em] text-gold'>
                            <span data-mdlfy-cms={`vehiculos:${vehicle.id}:marca`}>{vehicle.marca}</span>
                        </span>

                        <span className='mt-1.5 block font-heading text-[1.75rem] text-foreground decoration-gold/40 underline-offset-4 group-hover/title:underline'>
                            <span data-mdlfy-cms={`vehiculos:${vehicle.id}:modelo`}>{vehicle.modelo}</span>
                        </span>
                    </Link>
                </Heading>

                {vehicle.resumen && (
                    <p className='mt-3 line-clamp-2 text-sm leading-relaxed text-muted-foreground' data-mdlfy-cms={`vehiculos:${vehicle.id}:resumen`}>
                        {vehicle.resumen}
                    </p>
                )}

                <dl className='mt-5 flex flex-wrap gap-x-5 gap-y-2 text-sm text-foreground/80'>
                    <div className='flex items-center gap-2'>
                        <dt className='sr-only'>Capacidad</dt>
                        <Users className='size-4 text-gold' aria-hidden='true' />
                        <dd>
                            <span>{vehicle.pasajeros}</span> {pluralize(vehicle.pasajeros, 'pasajero', 'pasajeros')}
                        </dd>
                    </div>

                    {vehicle.maletas !== null && (
                        <div className='flex items-center gap-2'>
                            <dt className='sr-only'>Equipaje</dt>
                            <Luggage className='size-4 text-gold' aria-hidden='true' />
                            <dd>
                                <span>{vehicle.maletas}</span> {pluralize(vehicle.maletas, 'maleta', 'maletas')}
                            </dd>
                        </div>
                    )}
                </dl>

                {amenities.length > 0 && (
                    <ul className='mt-4 space-y-1.5 text-sm text-muted-foreground'>
                        {amenities.map(amenity => (
                            <li key={amenity} className='flex items-start gap-2'>
                                <Check className='mt-0.5 size-4 shrink-0 text-gold' aria-hidden='true' />
                                <span>{amenity}</span>
                            </li>
                        ))}
                    </ul>
                )}

                <div className='mt-auto pt-6'>
                    <div className='flex items-end justify-between gap-4 border-t border-border/80 pt-5'>
                        <div className='min-w-0'>
                            {vehicle.precio_hora !== null
                                ? (
                                    <>
                                        <p className='text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground'>
                                            Desde
                                        </p>

                                        <p className='mt-1 flex items-baseline gap-1'>
                                            <span className='font-heading text-[1.9rem] leading-none text-foreground'>{formatPrice(vehicle.precio_hora)}</span>
                                            <span className='text-sm text-muted-foreground'>/hora</span>
                                        </p>
                                    </>
                                )
                                : (
                                    <p className='font-heading text-xl text-foreground'>
                                        Precio a consultar
                                    </p>
                                )}

                            {vehicle.horas_minimas !== null && vehicle.horas_minimas > 1 && (
                                <p className='mt-1.5 text-xs text-muted-foreground'>
                                    Mínimo <span>{vehicle.horas_minimas}</span> horas
                                </p>
                            )}
                        </div>

                        <div className='flex shrink-0 flex-col items-end gap-2.5'>
                            {onReserve
                                ? (
                                    <button type='button' onClick={() => onReserve(vehicle)} className={reserveClass}>
                                        {reserveLabel}
                                    </button>
                                )
                                : (
                                    <Link href={`${href}#reservar`} className={reserveClass}>
                                        {reserveLabel}
                                    </Link>
                                )}

                            <Link href={href} className='inline-flex items-center gap-1 rounded-sm text-xs font-semibold text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'>
                                Ver detalles
                                <ArrowUpRight className='size-3.5' aria-hidden='true' />
                                <span className='sr-only'>
                                    de <span data-mdlfy-cms={`vehiculos:${vehicle.id}:marca`}>{vehicle.marca}</span> <span data-mdlfy-cms={`vehiculos:${vehicle.id}:modelo`}>{vehicle.modelo}</span>
                                </span>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </article>
    )
}
